import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import Navbar from '../../shared/navbar.js';
import './PropertyAddWizard.css';

const STEPS = [
  { id: 1, title: 'Property basics', short: 'Basics' },
  { id: 2, title: 'Unit types & floors', short: 'Unit types' },
  { id: 3, title: 'Legal templates', short: 'Legal' },
  { id: 4, title: 'Bill of expenses', short: 'Expenses' },
  { id: 5, title: 'Review & create', short: 'Review' },
  { id: 6, title: 'Set pricing', short: 'Pricing' },
];

function PropertyAddWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [templates, setTemplates] = useState([]);
  const [staff, setStaff] = useState([]);

  const [property, setProperty] = useState({
    property_name: '',
    address: '',
    unit_count: '',
    total_floors: '',
    property_type: '',
    property_status: 'active',
    year_built: '',
    total_square_footage: '',
    property_manager: '',
    maintenance_contact: '',
    maintenance_phone: '',
    purchase_price: '',
    current_market_value: '',
    monthly_expenses: '',
    description: '',
    neighborhood: '',
    parking: '',
    pet_policy: 'not allowed',
    last_inspection_date: '',
    next_inspection_date: '',
  });

  const [unitTypes, setUnitTypes] = useState([
    { type_name: '', bedroom_count: 1, bathroom_count: 1, unit_count: 1, square_footage: '', minimum_expected_rent: '', floor_rent_premium: '', description: '', min_floor: '', max_floor: '' },
  ]);

  const [repricing, setRepricing] = useState({
    enabled: false,
    frequency_days: 30,
    max_delta_pct: 5,
  });

  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [recurringExpenses, setRecurringExpenses] = useState([
    { expense_category: 'Utilities', expense_type: 'Recurring', description: 'Monthly estimate', amount: '', vendor_name: '' },
  ]);
  const [createdPropertyId, setCreatedPropertyId] = useState(null);

  useEffect(() => {
    const u = localStorage.getItem('username');
    if (!u) navigate('/');
  }, [navigate]);

  useEffect(() => {
    Promise.all([
        fetch(`${API_BASE_URL}/api/document-templates`).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${API_BASE_URL}/api/staff`).then(r => r.ok ? r.json() : []).catch(() => []),
      ]).then(([t, s]) => { setTemplates(t); setStaff(Array.isArray(s) ? s : []); });
  }, []);

  const updateProperty = (key, value) => setProperty(prev => ({ ...prev, [key]: value }));
  const addUnitType = () => setUnitTypes(prev => [...prev, { type_name: '', bedroom_count: 1, bathroom_count: 1, unit_count: 1, square_footage: '', minimum_expected_rent: '', floor_rent_premium: '', description: '', min_floor: '', max_floor: '' }]);
  const updateUnitType = (index, key, value) => setUnitTypes(prev => prev.map((ut, i) => i === index ? { ...ut, [key]: value } : ut));
  const removeUnitType = (index) => setUnitTypes(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);
  const addExpense = () => setRecurringExpenses(prev => [...prev, { expense_category: 'Other', expense_type: 'Recurring', description: '', amount: '', vendor_name: '' }]);
  const updateExpense = (index, key, value) => setRecurringExpenses(prev => prev.map((e, i) => i === index ? { ...e, [key]: value } : e));
  const removeExpense = (index) => setRecurringExpenses(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

  const toggleTemplate = (id) => setSelectedTemplateIds(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);

  const totalUnits = unitTypes.reduce((s, ut) => s + (parseInt(ut.unit_count, 10) || 0), 0);

  const PARKING_OPTIONS = ['lot', 'garage', 'street'];
  const parkingArray = (property.parking || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  const setParking = (option, checked) => {
    const next = checked ? [...new Set([...parkingArray, option])] : parkingArray.filter(o => o !== option);
    updateProperty('parking', next.join(', '));
  };

  const handleSubmit = async () => {
    setError('');
    if (!property.property_name?.trim()) { setError('Property name is required.'); return; }
    if (!property.address?.trim()) { setError('Address is required.'); return; }
    const validTypes = unitTypes.filter(ut => (ut.type_name?.trim() && (parseInt(ut.unit_count, 10) || 0) > 0));
    if (validTypes.length === 0) { setError('Add at least one unit type with name and unit count.'); return; }

    setSaving(true);
    try {
      const payload = {
        property: {
          ...property,
          unit_count: totalUnits,
          total_floors: property.total_floors ? parseInt(property.total_floors, 10) : null,
        },
        unit_types: validTypes.map(ut => ({
          type_name: ut.type_name,
          bedroom_count: parseInt(ut.bedroom_count, 10) || 1,
          bathroom_count: parseFloat(ut.bathroom_count) || 1,
          unit_count: parseInt(ut.unit_count, 10) || 0,
          square_footage: ut.square_footage ? parseInt(ut.square_footage, 10) : null,
          minimum_expected_rent: 0,
          floor_rent_premium: parseFloat(ut.floor_rent_premium) || 0,
          description: ut.description || '',
          min_floor: ut.min_floor !== '' ? parseInt(ut.min_floor, 10) : null,
          max_floor: ut.max_floor !== '' ? parseInt(ut.max_floor, 10) : null,
        })),
        repricing: {
          enabled: repricing.enabled ? 1 : 0,
          frequency_days: repricing.enabled ? (repricing.frequency_days || 30) : null,
          max_delta_pct: repricing.enabled ? (repricing.max_delta_pct || 5) : null,
        },
        legal_template_ids: selectedTemplateIds,
        recurring_expenses: recurringExpenses
          .filter(e => (e.expense_category || e.description) && (parseFloat(e.amount) || 0) > 0)
          .map(e => ({ ...e, amount: parseFloat(e.amount) || 0 })),
      };
      const res = await fetch(`${API_BASE_URL}/api/properties/wizard-create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create property');
      setCreatedPropertyId(data.property_id);
      setStep(6);
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar selected="Databases" />
      <div className="add-property-flow">
        <div className="add-property-flow__page">
          <div className="add-property-flow__card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h1 className="add-property-flow__title" style={{ margin: 0 }}>Add Property</h1>
              <button type="button" className="add-property-flow__close" aria-label="Close" onClick={() => navigate('/databases')}>×</button>
            </div>
            <div className="add-property-flow__steps" role="tablist" aria-label="Steps">
              {STEPS.map(s => (
                <button key={s.id} type="button" className={`add-property-flow__step-tab ${step === s.id ? 'add-property-flow__step-tab--active' : ''}`} onClick={() => setStep(s.id)}>
                  {s.id}. {s.short}
                </button>
              ))}
            </div>

            {error && <div className="add-property-flow__error">{error}</div>}

          {/* Step 1: Property basics */}
          {step === 1 && (
            <div className="add-property-flow__panel">
              <h2>Property basics</h2>
              <div className="add-property-flow__grid">
                <div className="add-property-flow__field">
                  <label>Property name *</label>
                  <input value={property.property_name} onChange={e => updateProperty('property_name', e.target.value)} placeholder="e.g. Sunset Apartments" />
                </div>
                <div className="add-property-flow__field add-property-flow__field--full">
                  <label>Address *</label>
                  <input value={property.address} onChange={e => updateProperty('address', e.target.value)} placeholder="Street, City, State ZIP" />
                </div>
                <div className="add-property-flow__field">
                  <label>Property type</label>
                  <select value={property.property_type} onChange={e => updateProperty('property_type', e.target.value)}>
                    <option value="">Select</option>
                    <option value="Apartment Complex">Apartment Complex</option>
                    <option value="Condo Building">Condo Building</option>
                    <option value="Townhouse Community">Townhouse Community</option>
                    <option value="Single Family">Single Family</option>
                    <option value="Mixed Use">Mixed Use</option>
                  </select>
                </div>
                <div className="add-property-flow__field">
                  <label>Total floors</label>
                  <input type="number" min="1" value={property.total_floors} onChange={e => updateProperty('total_floors', e.target.value)} placeholder="e.g. 5" />
                </div>
                <div className="add-property-flow__field">
                  <label>Year built</label>
                  <input type="number" value={property.year_built} onChange={e => updateProperty('year_built', e.target.value)} placeholder="e.g. 1995" />
                </div>
                <div className="add-property-flow__field">
                  <label>Total square footage</label>
                  <input type="number" min="0" value={property.total_square_footage} onChange={e => updateProperty('total_square_footage', e.target.value)} />
                </div>
                <div className="add-property-flow__field">
                  <label>Property manager</label>
                  <select value={property.property_manager || ''} onChange={e => updateProperty('property_manager', e.target.value)}>
                    <option value="">Select staff member</option>
                    {staff.map(s => (
                      <option key={s.id} value={s.name}>{s.name}{s.role ? ` (${s.role})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="add-property-flow__field">
                  <label>Maintenance contact</label>
                  <select value={property.maintenance_contact || ''} onChange={e => updateProperty('maintenance_contact', e.target.value)}>
                    <option value="">Select staff member</option>
                    {staff.map(s => (
                      <option key={s.id} value={s.name}>{s.name}{s.role ? ` (${s.role})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="add-property-flow__field">
                  <label>Maintenance phone</label>
                  <input value={property.maintenance_phone} onChange={e => updateProperty('maintenance_phone', e.target.value)} />
                </div>
                <div className="add-property-flow__field">
                  <label>Purchase price</label>
                  <input type="number" step="0.01" value={property.purchase_price} onChange={e => updateProperty('purchase_price', e.target.value)} />
                </div>
                <div className="add-property-flow__field">
                  <label>Current market value</label>
                  <input type="number" step="0.01" value={property.current_market_value} onChange={e => updateProperty('current_market_value', e.target.value)} />
                </div>
                <div className="add-property-flow__field">
                  <label>Monthly expenses (estimate)</label>
                  <input type="number" step="0.01" value={property.monthly_expenses} onChange={e => updateProperty('monthly_expenses', e.target.value)} />
                </div>
                <div className="form-field full">
                  <label>Description</label>
                  <textarea value={property.description} onChange={e => updateProperty('description', e.target.value)} rows={2} />
                </div>
                <div className="add-property-flow__field">
                  <label>Neighborhood</label>
                  <input value={property.neighborhood} onChange={e => updateProperty('neighborhood', e.target.value)} />
                </div>
                <div className="add-property-flow__field add-property-flow__field--full">
                  <label>Parking</label>
                  <div className="add-property-flow__check-group" role="group" aria-label="Parking options">
                    {PARKING_OPTIONS.map(opt => (
                      <label key={opt} className="add-property-flow__check">
                        <input type="checkbox" checked={parkingArray.includes(opt)} onChange={e => setParking(opt, e.target.checked)} />
                        <span>{opt.charAt(0).toUpperCase() + opt.slice(1)}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="add-property-flow__field">
                  <label>Pet policy</label>
                  <div className="add-property-flow__toggle-group" role="group" aria-label="Pet policy">
                    <button type="button" className={`add-property-flow__toggle-btn ${(property.pet_policy || '').toLowerCase() === 'allowed' ? 'add-property-flow__toggle-btn--active' : ''}`} onClick={() => updateProperty('pet_policy', 'allowed')}>Pets allowed</button>
                    <button type="button" className={`add-property-flow__toggle-btn ${(property.pet_policy || '').toLowerCase() !== 'allowed' ? 'add-property-flow__toggle-btn--active' : ''}`} onClick={() => updateProperty('pet_policy', 'not allowed')}>Pets not allowed</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Unit types */}
          {step === 2 && (
            <div className="add-property-flow__panel">
              <h2>Unit types & floors</h2>
              <p className="add-property-flow__hint">Define each unit type and how many units, plus which floors they’re on (min/max floor).</p>
              {unitTypes.map((ut, idx) => (
                <div key={idx} className="add-property-flow__unit-block">
                  <div className="add-property-flow__grid">
                    <div className="add-property-flow__field">
                      <label>Type name *</label>
                      <input value={ut.type_name} onChange={e => updateUnitType(idx, 'type_name', e.target.value)} placeholder="e.g. 1BR Classic" />
                    </div>
                    <div className="add-property-flow__field">
                      <label>Beds</label>
                      <input type="number" min="0" value={ut.bedroom_count} onChange={e => updateUnitType(idx, 'bedroom_count', e.target.value)} />
                    </div>
                    <div className="add-property-flow__field">
                      <label>Baths</label>
                      <input type="number" min="0" step="0.5" value={ut.bathroom_count} onChange={e => updateUnitType(idx, 'bathroom_count', e.target.value)} />
                    </div>
                    <div className="add-property-flow__field">
                      <label>Unit count *</label>
                      <input type="number" min="0" value={ut.unit_count} onChange={e => updateUnitType(idx, 'unit_count', e.target.value)} />
                    </div>
                    <div className="add-property-flow__field">
                      <label>Square footage</label>
                      <input type="number" min="0" value={ut.square_footage} onChange={e => updateUnitType(idx, 'square_footage', e.target.value)} />
                    </div>
                    <div className="add-property-flow__field">
                      <label>Min floor</label>
                      <input type="number" min="1" value={ut.min_floor} onChange={e => updateUnitType(idx, 'min_floor', e.target.value)} placeholder="1" />
                    </div>
                    <div className="add-property-flow__field">
                      <label>Max floor</label>
                      <input type="number" min="1" value={ut.max_floor} onChange={e => updateUnitType(idx, 'max_floor', e.target.value)} placeholder="e.g. 5" />
                    </div>
                    <div className="add-property-flow__field">
                      <button type="button" className="add-property-flow__btn-remove" onClick={() => removeUnitType(idx)}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" className="add-property-flow__btn-add" onClick={addUnitType}>+ Add unit type</button>
            </div>
          )}

          {/* Step 3: Legal templates */}
          {step === 3 && (
            <div className="add-property-flow__panel">
              <h2>Legal document templates</h2>
              <p className="add-property-flow__hint">Assign lease or other legal templates to this property (from Legal dashboard).</p>
              <div className="add-property-flow__template-list">
                {templates.length === 0 ? <p>No templates found. Create templates in the Legal dashboard first.</p> : (
                  templates.map(t => (
                    <label key={t.id} className="add-property-flow__check add-property-flow__template-item">
                      <input type="checkbox" checked={selectedTemplateIds.includes(t.id)} onChange={() => toggleTemplate(t.id)} />
                      <span>{t.name} {t.category && `(${t.category})`}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 4: Bill of expenses */}
          {step === 4 && (
            <div className="add-property-flow__panel">
              <h2>Detailed bill of expenses</h2>
              <p className="add-property-flow__hint">Enter all recurring monthly expenses (taxes, insurance, utilities, maintenance, etc.). These determine your <strong>per-unit minimum breakeven</strong>—rent must be above this to cover costs. You’ll set actual rents in the Pricing Engine at the end.</p>
              {recurringExpenses.map((e, idx) => (
                <div key={idx} className="add-property-flow__grid add-property-flow__expense-row">
                  <div className="add-property-flow__field">
                    <label>Category</label>
                    <select value={e.expense_category} onChange={ev => updateExpense(idx, 'expense_category', ev.target.value)}>
                      <option value="Utilities">Utilities</option>
                      <option value="Insurance">Insurance</option>
                      <option value="Taxes">Taxes</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="Management">Management</option>
                      <option value="HOA">HOA</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="add-property-flow__field">
                    <label>Description</label>
                    <input value={e.description} onChange={ev => updateExpense(idx, 'description', ev.target.value)} placeholder="e.g. Water, gas, electric" />
                  </div>
                  <div className="add-property-flow__field">
                    <label>Monthly amount ($)</label>
                    <input type="number" min="0" step="0.01" value={e.amount} onChange={ev => updateExpense(idx, 'amount', ev.target.value)} />
                  </div>
                  <div className="add-property-flow__field">
                    <label>Vendor</label>
                    <input value={e.vendor_name} onChange={ev => updateExpense(idx, 'vendor_name', ev.target.value)} />
                  </div>
                  <div className="add-property-flow__field">
                    <button type="button" className="add-property-flow__btn-remove" onClick={() => removeExpense(idx)}>Remove</button>
                  </div>
                </div>
              ))}
              <button type="button" className="add-property-flow__btn-add" onClick={addExpense}>+ Add expense</button>
              {(() => {
                const totalMonthly = recurringExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
                const breakevenPerUnit = totalUnits > 0 ? totalMonthly / totalUnits : 0;
                return (
                  <div className="add-property-flow__review" style={{ marginTop: 20 }}>
                    <p><strong>Total monthly expenses:</strong> ${totalMonthly.toFixed(2)}</p>
                    <p><strong>Per-unit minimum breakeven:</strong> ${breakevenPerUnit.toFixed(2)}/mo (rent must be above this)</p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Step 5: Review */}
          {step === 5 && (
            <div className="add-property-flow__panel">
              <h2>Review & create</h2>
              <div className="add-property-flow__review">
                <section>
                  <h3>Property</h3>
                  <p><strong>{property.property_name || '—'}</strong></p>
                  <p>{property.address || '—'}</p>
                  <p>Type: {property.property_type || '—'} · Floors: {property.total_floors || '—'}</p>
                </section>
                <section>
                  <h3>Unit types ({unitTypes.length})</h3>
                  <ul>
                    {unitTypes.map((ut, i) => (
                      <li key={i}>{ut.type_name || `Type ${i + 1}`}: {ut.unit_count} units · Rent set in Pricing Engine</li>
                    ))}
                  </ul>
                  <p>Total units: {totalUnits}</p>
                </section>
                <section>
                  <h3>Repricing</h3>
                  <label className="add-property-flow__check">
                    <input type="checkbox" checked={repricing.enabled} onChange={e => setRepricing(prev => ({ ...prev, enabled: e.target.checked }))} />
                    Enable automatic repricing
                  </label>
                  {repricing.enabled && (
                    <div className="add-property-flow__grid" style={{ marginTop: 8 }}>
                      <div className="add-property-flow__field">
                        <label>Frequency (days)</label>
                        <input type="number" min="1" value={repricing.frequency_days} onChange={e => setRepricing(prev => ({ ...prev, frequency_days: e.target.value }))} />
                      </div>
                      <div className="add-property-flow__field">
                        <label>Max change %</label>
                        <input type="number" min="0" step="0.5" value={repricing.max_delta_pct} onChange={e => setRepricing(prev => ({ ...prev, max_delta_pct: e.target.value }))} />
                      </div>
                    </div>
                  )}
                </section>
                <section>
                  <h3>Legal templates</h3>
                  <p>{selectedTemplateIds.length} selected</p>
                </section>
                <section>
                  <h3>Bill of expenses</h3>
                  <p>{recurringExpenses.filter(e => (e.expense_category || e.description) && (parseFloat(e.amount) || 0) > 0).length} entries · Total ${recurringExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0).toFixed(2)}/mo · Breakeven ${totalUnits > 0 ? (recurringExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0) / totalUnits).toFixed(2) : '0'}/unit/mo</p>
                </section>
              </div>
            </div>
          )}

          {/* Step 6: Pricing (after create — go to Pricing Engine) */}
          {step === 6 && createdPropertyId && (
            <div className="add-property-flow__panel">
              <h2>Set pricing</h2>
              <p className="add-property-flow__hint">Your property is created. Set rents in the <strong>Pricing Engine</strong>—rent should be above your per-unit breakeven to cover expenses.</p>
              {(() => {
                const totalMonthly = recurringExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
                const breakevenPerUnit = totalUnits > 0 ? totalMonthly / totalUnits : 0;
                return (
                  <div className="add-property-flow__review">
                    <section>
                      <h3>Per-unit minimum breakeven</h3>
                      <p><strong>${breakevenPerUnit.toFixed(2)}/mo</strong> (from your bill of expenses). Set rent at or above this in the Pricing Engine.</p>
                    </section>
                  </div>
                );
              })()}
              <div className="add-property-flow__actions" style={{ marginTop: 24, borderTop: 'none' }}>
                <button type="button" className="add-property-flow__btn add-property-flow__btn--primary" onClick={() => navigate(`/pricing-engine?propertyId=${createdPropertyId}`)}>
                  Open Pricing Engine
                </button>
                <button type="button" className="add-property-flow__btn add-property-flow__btn--secondary" onClick={() => navigate(`/property/${createdPropertyId}`)}>
                  View property
                </button>
              </div>
            </div>
          )}

          <div className="add-property-flow__actions">
            {step > 1 && step < 6 && <button type="button" className="add-property-flow__btn add-property-flow__btn--secondary" onClick={() => setStep(step - 1)}>Back</button>}
            {step < 5 && <button type="button" className="add-property-flow__btn add-property-flow__btn--primary" onClick={() => setStep(step + 1)}>Next</button>}
            {step === 5 && (
              <button type="button" className="add-property-flow__btn add-property-flow__btn--primary" disabled={saving} onClick={handleSubmit}>
                {saving ? 'Creating…' : 'Create property'}
              </button>
            )}
          </div>
        </div>
        </div>
      </div>
    </>
  );
}

export default PropertyAddWizard;
